import {ActionEvent, Controller} from "stimulus";
import {SkippableRange} from "./skippable_range";

/*
  Add this controller to a form to allow the form to be split into multiple
  steps.  This allows for a more guided experience for users when filling out
  complex forms.
  */
export default class extends Controller<HTMLElement> {
  static targets = ["step", "nextButton", "previousButton", "submitButton", "error", "currentStep", "totalSteps"];
  static values = {hiddenQuestionClass: String};

  declare stepTargets: HTMLElement[];
  declare nextButtonTarget: HTMLElement;
  declare previousButtonTarget: HTMLElement;
  declare submitButtonTarget: HTMLElement;
  declare hiddenQuestionClassValue: string;
  declare errorTargets: HTMLElement[];
  declare hasErrorTarget: boolean;
  declare currentStepTarget: HTMLElement;
  declare totalStepsTarget: HTMLElement;

  private stepRange!: SkippableRange;

  connect() {
    this.stepRange = new SkippableRange(this.stepTargets.length - 1, 0, []);
    this.updateSkippableSteps();
    this.setStepToFirstError();
    this.refreshDisplay();
  }

  next() {
    this.stepRange.next();
    this.refreshDisplay();
  }

  previous() {
    this.stepRange.previous();
    this.refreshDisplay();
  }

  /*
    Event handler for reacting to question visibility changes.

    Allows us to change button status without waiting for navigation changes.
    */
  toggleQuestion(_event: CustomEvent<{visible: boolean; element: HTMLElement}>) {
    this.updateSkippableSteps();
    this.updateButtons();
    this.updateProgress();
  }

  openStep({params}: ActionEvent) {
    const inputId = params.inputId;
    if (!inputId) return;

    this.openStepContainingInput(inputId);
  }

  private setStepToFirstError() {
    if (this.hasErrorTarget) {
      this.openStepContainingInput(this.errorTargets[0].dataset.multiStepFormInputIdParam!);
    }
  }

  private openStepContainingInput(inputId: string) {
    const stepIndex = this.stepTargets.findIndex(step => step.querySelector(`#${inputId}`));
    if (stepIndex === -1) return;

    if (this.stepRange.setCurrent(stepIndex)) {
      this.refreshDisplay();
    }
  }

  private refreshDisplay() {
    this.showCurrentStep();
    this.updateButtons();
    this.updateProgress();
  }

  private showCurrentStep() {
    const currentStep = this.stepRange.getCurrent();
    this.stepTargets.forEach((element, index) => {
      element.hidden = index !== currentStep;
    });
  }

  /*
    Scans all steps and updates the SkippableRange with which steps should be skipped.
    A step is skipped if all of its children have the hidden question class.
    */
  private updateSkippableSteps() {
    const skippableIndexes = this.stepTargets
      .map((_, index) => index)
      .filter(index => this.isStepSkipped(index));
    this.stepRange.setSkippable(skippableIndexes);
  }

  private updateButtons() {
    this.setButtonVisibility(this.previousButtonTarget, !this.isFirstStep());
    this.setButtonVisibility(this.nextButtonTarget, !this.isLastStep());
    this.setButtonVisibility(this.submitButtonTarget, this.isLastStep());
  }

  private setButtonVisibility(button: HTMLElement, displayCondition: boolean) {
    if (!displayCondition) {
      button.classList.add("hidden");
    } else {
      button.classList.remove("hidden");
    }
  }

  private isFirstStep() {
    return this.stepRange.isAtFirst();
  }

  private isLastStep() {
    return this.stepRange.isAtLast();
  }

  private isStepSkipped(stepIndex: number) {
    return Array.from(this.stepTargets[stepIndex].children).every((child: HTMLElement) => child.classList.contains(this.hiddenQuestionClassValue));
  }

  private updateProgress() {
    const applicableValues = this.stepRange.getApplicableValues();
    const currentIndex = applicableValues.indexOf(this.stepRange.getCurrent());
    this.currentStepTarget.textContent = `${currentIndex + 1}`;
    this.totalStepsTarget.textContent = `${applicableValues.length}`;
  }
}
